import { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_BADGE = {
  violation: { color: '#dc2626', label: 'Violation' },
  news: { color: '#0891b2', label: 'News' },
  shift_swap: { color: '#7c3aed', label: 'Shift' },
  message: { color: '#16a34a', label: 'Message' },
  approval: { color: '#C9A962', label: 'Approval' },
  info: { color: '#525252', label: 'Info' },
};

export default function NotificationsBell({ variant = 'light' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const popRef = useRef(null);

  const isDark = variant === 'dark';
  const btnClass = isDark
    ? 'relative p-2 rounded-full hover:bg-white/10 text-white'
    : 'relative p-2 rounded-full hover:bg-zinc-100 text-zinc-700';

  const load = async () => {
    if (!user) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/notifications/summary`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch (_) {
      /* silent */
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(id);
     
  }, [user?.user_id]);

  useEffect(() => {
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markAllRead = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/read-all`, { method: 'POST', credentials: 'include' });
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      setUnread(0);
    } catch (_) {
      /* silent */
    }
  };
  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={btnClass}
        aria-label="Notifications"
        data-testid="notifications-bell"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center text-white"
            style={{ backgroundColor: '#dc2626' }}
            data-testid="notifications-unread-count"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-h-[70vh] overflow-hidden rounded-lg shadow-2xl bg-white"
          style={{ border: '1px solid #e4e4e7' }}
          data-testid="notifications-popover"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-zinc-50" style={{ borderBottom: '1px solid #e4e4e7' }}>
            <p className="text-sm font-bold text-zinc-900">Notifications</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-xs font-semibold hover:underline"
                style={{ color: '#C9A962' }}
                data-testid="notifications-mark-all-read"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto max-h-[60vh]">
            {items.length === 0 ? (
              <p className="text-sm text-zinc-500 px-4 py-8 text-center" data-testid="notifications-empty">
                No notifications yet. We&apos;ll alert you here when emails, messages or violation reports come in.
              </p>
            ) : (
              items.map((n) => {
                const meta = KIND_BADGE[n.kind] || KIND_BADGE.info;
                return (
                  <div
                    key={n.notification_id}
                    className={`px-4 py-3 flex gap-3 ${n.read ? 'bg-white' : 'bg-amber-50'}`}
                    style={{ borderBottom: '1px solid #f4f4f5' }}
                    data-testid={`notification-row-${n.notification_id}`}
                  >
                    <span
                      className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-1 h-fit text-white flex-shrink-0"
                      style={{ backgroundColor: meta.color }}
                    >
                      {meta.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 break-words">{n.title}</p>
                      {n.body && <p className="text-xs text-zinc-600 mt-0.5 line-clamp-3">{n.body}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[10px] text-zinc-400">{new Date(n.created_at).toLocaleString()}</p>
                        {n.target_department && (
                          <span
                            className="text-[9px] uppercase tracking-wider font-bold rounded-full px-1.5 py-[1px] text-zinc-600 bg-zinc-100 border border-zinc-200"
                            data-testid={`notification-dept-${n.target_department}`}
                          >
                            {n.target_department}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
